class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  static OPEN_DELAY = 100;
  static CLOSE_DELAY = 250;
  static CLOSE_ANIMATION_DURATION = 200;

  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.summary = this.mainDetailsToggle.querySelector('summary');
    this.hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.summary.addEventListener('click', this.onSummaryClick.bind(this));
    this.addEventListener('pointerenter', this.onPointerEnter.bind(this));
    this.addEventListener('pointerleave', this.onPointerLeave.bind(this));
  }

  get isOpen() {
    return this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.classList.contains('is-closing');
  }

  onSummaryClick(event) {
    event.preventDefault();

    // A click straight after a hover-open should keep the menu open rather than toggle it shut
    if (this.isOpen && this.openedByHover) {
      this.openedByHover = false;
      this.summary.setAttribute('aria-expanded', true);
      return;
    }

    this.isOpen ? this.close() : this.open();
  }

  onPointerEnter(event) {
    if (event.pointerType !== 'mouse' || !this.hoverQuery.matches) return;

    clearTimeout(this.hoverCloseTimer);
    if (this.isOpen) return;

    const isSwitching = Array.from(document.querySelectorAll('header-menu')).some(
      (menu) => menu !== this && menu.isOpen
    );
    if (isSwitching || this.mainDetailsToggle.classList.contains('is-closing')) {
      this.open({ viaHover: true, switching: true });
      return;
    }

    clearTimeout(this.hoverOpenTimer);
    this.hoverOpenTimer = setTimeout(() => this.open({ viaHover: true }), HeaderMenu.OPEN_DELAY);
  }

  onPointerLeave(event) {
    if (event.pointerType !== 'mouse' || !this.hoverQuery.matches) return;

    clearTimeout(this.hoverOpenTimer);
    if (!this.isOpen) return;

    clearTimeout(this.hoverCloseTimer);
    this.hoverCloseTimer = setTimeout(() => this.close(), HeaderMenu.CLOSE_DELAY);
  }

  open({ viaHover = false, switching = false } = {}) {
    this.clearTimers();

    document.querySelectorAll('header-menu').forEach((menu) => {
      if (menu !== this) menu.close({ instant: true });
    });

    this.openedByHover = viaHover;
    this.mainDetailsToggle.classList.remove('is-closing');
    this.mainDetailsToggle.classList.toggle('is-switching', switching);
    this.mainDetailsToggle.setAttribute('open', '');
    this.summary.setAttribute('aria-expanded', true);
    this.syncScrim();
  }

  close({ instant = false } = {}) {
    this.clearTimers();
    this.openedByHover = false;
    this.summary.setAttribute('aria-expanded', false);

    if (!this.mainDetailsToggle.hasAttribute('open')) return;

    this.mainDetailsToggle.classList.remove('is-switching');

    if (instant || this.reducedMotionQuery.matches) {
      this.finishClose();
      return;
    }

    this.mainDetailsToggle.classList.add('is-closing');
    this.syncScrim();
    this.closeAnimationTimer = setTimeout(() => this.finishClose(), HeaderMenu.CLOSE_ANIMATION_DURATION);
  }

  finishClose() {
    this.mainDetailsToggle.classList.remove('is-closing');
    this.mainDetailsToggle.removeAttribute('open');
    this.syncScrim();
  }

  clearTimers() {
    clearTimeout(this.hoverOpenTimer);
    clearTimeout(this.hoverCloseTimer);
    clearTimeout(this.closeAnimationTimer);
  }

  syncScrim() {
    const sectionHeader = this.closest('.section-header');
    if (!sectionHeader) return;

    sectionHeader.classList.toggle(
      'section-header--menu-open',
      !!sectionHeader.querySelector('details.mega-menu[open]:not(.is-closing)')
    );
  }

  onToggle() {
    if (!this.header) return;
    const isOpen = this.mainDetailsToggle.open;
    this.header.preventHide = isOpen;

    // Menus can also be closed directly (e.g. Escape in global.js), so tidy up state here too
    if (!isOpen) this.mainDetailsToggle.classList.remove('is-closing', 'is-switching');
    this.syncScrim();

    // Re-measure on every open: the header height can change between opens
    if (!isOpen) return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);
